'use client';

import { X } from 'lucide-react';
import type { TextBlock } from '@/features/drawing/model';
import { TEXT_BLOCK_CONSTRAINTS } from './constants';
import { ResizeHandles } from './ResizeHandles';
import type {
  TextBlockPosition,
  ResizeHandle,
  PotentialDragState,
} from './types';

interface TextBlockItemProps {
  block: TextBlockPosition;
  isActive: boolean;
  isHovered: boolean;
  showHoverEffect: boolean;
  isMobile: boolean;
  isOverDraggableArea: boolean;
  zoom: number;
  textBlocks: TextBlock[];
  tool: string | null;
  // Refs
  editableRefs: React.RefObject<Map<string, HTMLDivElement>>;
  // Handlers
  onBlockClick: (e: React.MouseEvent, blockId: string) => void;
  onDelete: (blockId: string) => void;
  onTextInput: (blockId: string, e: React.FormEvent<HTMLDivElement>) => void;
  onDragStart: (e: React.MouseEvent, block: TextBlock) => void;
  onTouchDragStart: (e: React.TouchEvent, block: TextBlock) => void;
  onResizeStart: (
    e: React.MouseEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
  onTouchResizeStart: (
    e: React.TouchEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
  // Hover/drag area state setters
  setHoveredBlockId: (id: string | null) => void;
  setIsOverDraggableArea: (id: string | null) => void;
  // Potential drag handling
  potentialDrag: PotentialDragState | null;
  setPotentialDrag: React.Dispatch<
    React.SetStateAction<PotentialDragState | null>
  >;
  startDragFromPotential: (
    block: TextBlock,
    potentialDrag: PotentialDragState
  ) => void;
}

export function TextBlockItem({
  block,
  isActive,
  isHovered: _isHovered,
  showHoverEffect,
  isMobile,
  isOverDraggableArea,
  zoom,
  textBlocks,
  tool,
  editableRefs,
  onBlockClick,
  onDelete,
  onTextInput,
  onDragStart,
  onTouchDragStart,
  onResizeStart,
  onTouchResizeStart,
  setHoveredBlockId,
  setIsOverDraggableArea,
  potentialDrag,
  setPotentialDrag,
  startDragFromPotential,
}: TextBlockItemProps) {
  // On desktop: only show 'move' cursor when over draggable areas (border/handle)
  // On mobile: always 'move' for active blocks (touch drag works from anywhere)
  const getCursor = () => {
    if (isActive) {
      if (isMobile) {
        return 'move';
      }
      return isOverDraggableArea ? 'move' : 'default';
    }
    return tool === 'text' ? 'pointer' : 'default';
  };

  // Calculate sizes that stay constant on screen (inverse scaling)
  const outlineWidthPx = 2;
  const paddingPx = 15;
  const outlineWidth = outlineWidthPx / zoom;
  const padding = paddingPx / zoom;
  const paddingCompensation = paddingPx;
  const totalCompensation = paddingCompensation;

  return (
    <div
      className="absolute pointer-events-auto"
      style={{
        left: `${block.screenX - totalCompensation}px`,
        top: `${block.screenY - totalCompensation}px`,
        width: block.isManualWidth ? `${block.width}px` : 'fit-content',
        minWidth: block.isManualWidth ? `${block.width}px` : undefined,
        maxWidth: block.isManualWidth ? `${block.width}px` : `${400 / zoom}px`,
        height: 'auto',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left',
        outline: isActive ? `${outlineWidth}px solid #14b8a6` : 'none',
        outlineOffset: isActive ? '0px' : undefined,
        backgroundColor: showHoverEffect
          ? 'rgba(20, 184, 166, 0.1)'
          : 'transparent',
        cursor: getCursor(),
        padding: `${padding}px`,
        boxSizing: 'content-box',
        zIndex: isActive ? 30 : 20,
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      onClick={(e) => {
        if (tool === 'text' && !isActive) {
          onBlockClick(e, block.id);
        }
      }}
      onMouseEnter={(e) => {
        setHoveredBlockId(block.id);
        if (!isMobile && isActive) {
          const target = e.currentTarget;
          const rect = target.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const nearBorder =
            x < 20 || y < 20 || x > rect.width - 20 || y > rect.height - 20;
          if (nearBorder) {
            setIsOverDraggableArea(block.id);
          }
        }
      }}
      onMouseMove={(e) => {
        if (!isMobile && isActive) {
          const target = e.currentTarget;
          const rect = target.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const nearBorder =
            x < 20 || y < 20 || x > rect.width - 20 || y > rect.height - 20;
          if (nearBorder) {
            if (!isOverDraggableArea) {
              setIsOverDraggableArea(block.id);
            }
          } else {
            if (isOverDraggableArea) {
              setIsOverDraggableArea(null);
            }
          }
        }
      }}
      onMouseLeave={() => {
        setHoveredBlockId(null);
        setIsOverDraggableArea(null);
      }}
      onMouseDown={(e) => {
        if (isActive) {
          if (!isMobile) {
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const nearBorder =
              x < 20 || y < 20 || x > rect.width - 20 || y > rect.height - 20;
            if (nearBorder) {
              onDragStart(e, block);
            }
          } else {
            onDragStart(e, block);
          }
        }
      }}
      onTouchStart={(e) => {
        if (e.touches.length > 1) {
          return;
        }
        if (isActive) {
          onTouchDragStart(e, block);
        } else if (tool === 'text') {
          e.preventDefault();
          onBlockClick(e as unknown as React.MouseEvent, block.id);
        }
      }}
    >
      {/* Contenteditable text area */}
      <div
        ref={(el) => {
          if (el) {
            editableRefs.current.set(block.id, el);
            if (block.text.length === 0) {
              if (!el.querySelector('br')) {
                el.innerHTML = '<br>';
              }
            } else {
              if (el.innerText !== block.text) {
                el.innerText = block.text;
              }
            }
          } else {
            editableRefs.current.delete(block.id);
          }
        }}
        contentEditable={isActive}
        tabIndex={isActive ? 0 : -1}
        suppressContentEditableWarning
        spellCheck={false}
        role="textbox"
        aria-multiline="true"
        inputMode="text"
        data-block-id={block.id}
        onInput={(e) => onTextInput(block.id, e)}
        onBeforeInput={(e) => {
          const target = e.currentTarget as HTMLDivElement;
          if (
            target.innerText.length >= TEXT_BLOCK_CONSTRAINTS.MAX_CHARS &&
            e.data
          ) {
            e.preventDefault();
          }
        }}
        onMouseEnter={() => {
          if (!isMobile && isActive) {
            setIsOverDraggableArea(null);
          }
        }}
        onMouseDown={(e) => {
          if (isActive) {
            e.stopPropagation();
            const target = e.currentTarget as HTMLDivElement;
            target.focus();
          } else if (tool === 'text') {
            e.stopPropagation();
            onBlockClick(e as unknown as React.MouseEvent, block.id);
          }
        }}
        onTouchStart={(e) => {
          if (e.touches.length > 1) {
            return;
          }
          if (!isActive && tool === 'text') {
            e.stopPropagation();
            e.preventDefault();
            onBlockClick(e as unknown as React.MouseEvent, block.id);
          } else if (isActive) {
            e.stopPropagation();
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) {
              setPotentialDrag({
                blockId: block.id,
                startX: touch.clientX,
                startY: touch.clientY,
                startTime: Date.now(),
              });
            }
          }
        }}
        onTouchMove={(e) => {
          if (e.touches.length > 1) {
            return;
          }
          if (potentialDrag && potentialDrag.blockId === block.id) {
            const touch = e.touches[0];
            if (touch) {
              const dx = touch.clientX - potentialDrag.startX;
              const dy = touch.clientY - potentialDrag.startY;
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance > 5) {
                e.preventDefault();
                e.stopPropagation();
                startDragFromPotential(block, potentialDrag);
              }
            }
          }
        }}
        onTouchEnd={(e) => {
          if (e.touches.length > 0) {
            return;
          }
          if (potentialDrag && potentialDrag.blockId === block.id) {
            setPotentialDrag(null);
            const target = e.currentTarget as HTMLDivElement;
            setTimeout(() => {
              target.focus();
            }, 50);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="outline-none"
        style={{
          fontSize: '16px',
          fontFamily: 'sans-serif',
          fontWeight: 'bold',
          lineHeight: '22px',
          display: 'inline-block',
          verticalAlign: 'top',
          width: block.isManualWidth ? '100%' : 'fit-content',
          maxWidth: block.isManualWidth
            ? `${block.width}px`
            : `${400 / zoom}px`,
          overflow: 'visible',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          minHeight: '22px',
          userSelect: isActive ? 'text' : 'none',
          WebkitUserSelect: isActive ? 'text' : 'none',
          WebkitTapHighlightColor: 'transparent',
          color: showHoverEffect ? '#14b8a6' : '#000000',
          touchAction: 'manipulation',
          cursor: isActive ? 'text' : 'default',
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
          padding: '4px 6px',
          borderRadius: '2px',
        }}
      />

      {isActive && (
        <>
          <ResizeHandles
            blockId={block.id}
            zoom={zoom}
            textBlocks={textBlocks}
            onDragStart={onDragStart}
            onTouchDragStart={onTouchDragStart}
            onResizeStart={onResizeStart}
            onTouchResizeStart={onTouchResizeStart}
          />

          {/* Delete button */}
          <button
            className="absolute bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(block.id);
            }}
            style={{
              width: `${28 / zoom}px`,
              height: `${28 / zoom}px`,
              top: `${-14 / zoom}px`,
              right: `${-14 / zoom}px`,
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            <X size={16 / zoom} />
          </button>
        </>
      )}
    </div>
  );
}
